import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, Zap, Users, Target, Calendar, Clock } from 'lucide-react';
interface InitiateAccessPageProps {
  onNavigate?: (path: string) => void;
}
// Declare Paystack on window object
declare global {
  interface Window {
    PaystackPop: any;
  }
}
export function InitiateAccessPage({
  onNavigate
}: InitiateAccessPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<'group' | 'oneOnOne'>('group');
  const [oneOnOnePaymentOption, setOneOnOnePaymentOption] = useState<'annual' | 'split'>('annual');
  const handlePayment = () => {
    if (!email || !name) {
      alert('Please enter your name and email to proceed with payment');
      setShowForm(true);
      return;
    }
    let amount: number;
    let programName: string;
    let paymentDescription: string;
    if (selectedProgram === 'group') {
      amount = 7000000; // ₦70,000
      programName = 'The Authority Engine (Group Program)';
      paymentDescription = 'Group Program - Full Payment';
    } else {
      // One-on-One Drilling Pot
      if (oneOnOnePaymentOption === 'annual') {
        amount = 70000000; // ₦700,000
        paymentDescription = 'One-on-One - Annual Payment';
      } else {
        amount = 35000000; // ₦350,000 (50% upfront)
        paymentDescription = 'One-on-One - Split Payment (50% Upfront)';
      }
      programName = 'The Drilling Pot (One-on-One)';
    }
    // Paystack integration
    const handler = window.PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: email,
      amount: amount,
      currency: 'NGN',
      ref: (selectedProgram === 'group' ? 'AE_' : 'DP_') + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: name
        }, {
          display_name: 'Program',
          variable_name: 'program',
          value: programName
        }, {
          display_name: 'Payment Description',
          variable_name: 'payment_description',
          value: paymentDescription
        }]
      },
      callback: function (response: any) {
        // Payment successful - redirect to WhatsApp community
        if (selectedProgram === 'oneOnOne' && oneOnOnePaymentOption === 'split') {
          alert('First payment received! You will be contacted about the second payment (₦350,000) at the beginning of month 2. You will now be redirected to join our WhatsApp community.');
        } else {
          alert('Payment successful! You will now be redirected to join our WhatsApp community.');
        }
        window.location.href = 'https://chat.whatsapp.com/CPhnSFkreyVJl4M8AF8zZo';
      },
      onClose: function () {
        // Payment cancelled
        console.log('Payment cancelled');
      }
    });
    handler.openIframe();
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-1123">
      {/* Load Paystack Script */}
      <script src="https://js.paystack.co/v1/inline.js" data-id="element-1124"></script>

      <Navbar onNavigate={onNavigate} data-id="element-1125" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-1126" />

      {/* Story Section */}
      <section className="py-24" data-id="element-1127">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-1128">
          <div className="text-center mb-16" data-id="element-1129">
            <h1 className="text-4xl md:text-6xl font-bold text-[#1f2933] mb-6" data-id="element-1130">
              Your Authority Journey Begins Here
            </h1>
            <p className="text-lg font-mono text-salon-red" data-id="element-1131">
              This is not a course. This is your operating system for authority.
            </p>
          </div>

          {/* Program Details */}
          <div className="bg-salon-yellow/10 border-2 border-salon-yellow p-8 mb-12" data-id="element-1132">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-1133">
              <Calendar className="w-6 h-6 text-salon-yellow" data-id="element-1134" />
              Program Structure
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1135">
              <div data-id="element-1136">
                <h3 className="font-bold mb-2 flex items-center gap-2" data-id="element-1137">
                  <Clock className="w-4 h-4 text-salon-yellow" data-id="element-1138" />
                  Duration
                </h3>
                <p className="text-sm text-[#1f2933]/80 font-mono" data-id="element-1139">
                  2 months intensive training
                  <br data-id="element-1140" />
                  Weekends (Sunday evenings)
                  <br data-id="element-1141" />
                  <span className="text-xs text-[#9ca3af]" data-id="element-1142">
                    *Time may be adjusted based on community consensus
                  </span>
                </p>
              </div>
              <div data-id="element-1143">
                <h3 className="font-bold mb-2" data-id="element-1144">Focus Areas</h3>
                <ul className="text-sm text-[#1f2933]/80 space-y-1" data-id="element-1145">
                  <li data-id="element-1146">• Brand goals & objectives clarity</li>
                  <li data-id="element-1147">• Target audience identification</li>
                  <li data-id="element-1148">• Content framework development</li>
                  <li data-id="element-1149">• AI-powered content machine setup</li>
                  <li data-id="element-1150">• Consistency systems & accountability</li>
                </ul>
              </div>
            </div>
          </div>

          {/* The Story */}
          <div className="space-y-12 mb-16" data-id="element-1151">
            <div className="border-l-4 border-salon-red pl-6" data-id="element-1152">
              <h2 className="text-2xl font-bold mb-4" data-id="element-1153">
                The Problem You're Facing
              </h2>
              <p className="text-lg leading-relaxed text-[#1f2933]/80 mb-4" data-id="element-1154">
                You're a founder, leader, or consultant with valuable expertise.
                You know content builds authority. You've tried posting
                consistently. But between running your business, serving
                clients, and managing life, content creation becomes another
                task that slips through the cracks.
              </p>
              <p className="text-lg leading-relaxed text-[#1f2933]/80" data-id="element-1155">
                The problem isn't motivation. It's infrastructure.
              </p>
            </div>

            <div className="border-l-4 border-salon-teal pl-6" data-id="element-1156">
              <h2 className="text-2xl font-bold mb-4" data-id="element-1157">
                What The Authority Engine Does
              </h2>
              <p className="text-lg leading-relaxed text-[#1f2933]/80 mb-4" data-id="element-1158">
                We replace chaos with control. We give you a complete system
                that turns your expertise into consistent, high-quality content
                that builds authority, visibility, and income.
              </p>
              <p className="text-lg leading-relaxed text-[#1f2933]/80 font-bold" data-id="element-1159">
                You'll learn to create your own content framework and build your
                personal content machine that generates ideas, drafts, and
                polished pieces on autopilot.
              </p>
            </div>

            <div className="border-l-4 border-salon-yellow pl-6" data-id="element-1160">
              <h2 className="text-2xl font-bold mb-4" data-id="element-1161">How It Works</h2>
              <div className="space-y-4" data-id="element-1162">
                <div className="flex items-start gap-4" data-id="element-1163">
                  <Check className="w-6 h-6 text-salon-red flex-shrink-0 mt-1" data-id="element-1164" />
                  <div data-id="element-1165">
                    <h3 className="font-bold mb-2" data-id="element-1166">
                      Build Your Content Framework
                    </h3>
                    <p className="text-[#1f2933]/80" data-id="element-1167">
                      We help you design a custom content framework that aligns
                      with your expertise, audience, and business goals. This
                      becomes your blueprint for all content creation.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1168">
                  <Zap className="w-6 h-6 text-salon-teal flex-shrink-0 mt-1" data-id="element-1169" />
                  <div data-id="element-1170">
                    <h3 className="font-bold mb-2" data-id="element-1171">
                      Create Your Content Machine
                    </h3>
                    <p className="text-[#1f2933]/80" data-id="element-1172">
                      Learn to leverage AI tools while maintaining your
                      authentic voice. Build systems that generate endless
                      ideas, create first drafts, and streamline your entire
                      workflow.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1173">
                  <Users className="w-6 h-6 text-salon-yellow flex-shrink-0 mt-1" data-id="element-1174" />
                  <div data-id="element-1175">
                    <h3 className="font-bold mb-2" data-id="element-1176">Join Accountability Pods</h3>
                    <p className="text-[#1f2933]/80" data-id="element-1177">
                      Small groups of peers who keep you consistent through
                      structured pressure, feedback, and shared momentum.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1178">
                  <Target className="w-6 h-6 text-salon-red flex-shrink-0 mt-1" data-id="element-1179" />
                  <div data-id="element-1180">
                    <h3 className="font-bold mb-2" data-id="element-1181">Compound Your Authority</h3>
                    <p className="text-[#1f2933]/80" data-id="element-1182">
                      Watch as consistent, strategic content transforms into
                      visibility, opportunities, and revenue streams.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Investment Section */}
          <div id="payment" className="bg-white border-2 border-dashed border-[#1f2933] p-8 md:p-12 scroll-mt-24" data-id="element-1183">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1184">
              Choose Your Path
            </h2>

            {/* Two Pricing Cards Side by Side */}
            <div className="grid md:grid-cols-2 gap-8 mb-10" data-id="element-1185">
              {/* Group Program Card */}
              <div className={`border-2 p-8 cursor-pointer transition-all ${selectedProgram === 'group' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setSelectedProgram('group')} data-id="element-1186">
                <div className="flex justify-between items-start mb-6" data-id="element-1187">
                  <div className="w-6 h-6 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1188">
                    {selectedProgram === 'group' && <div className="w-4 h-4 rounded-full bg-salon-red" data-id="element-1189" />}
                  </div>
                  <div className="bg-salon-red text-white px-3 py-1 text-xs font-mono" data-id="element-1190">
                    POPULAR
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4" data-id="element-1191">Group Program</h3>

                <div className="mb-6" data-id="element-1192">
                  <div className="flex items-center justify-center gap-3 mb-2" data-id="element-1193">
                    <span className="text-xl text-[#9ca3af] line-through font-mono" data-id="element-1194">
                      ₦500,000
                    </span>
                  </div>
                  <div className="bg-salon-red text-white py-2 px-4 inline-block mb-2" data-id="element-1195">
                    <span className="text-3xl font-bold" data-id="element-1196">₦70,000</span>
                  </div>
                  <p className="font-mono text-xs text-[#9ca3af]" data-id="element-1197">
                    Limited time offer
                  </p>
                </div>

                <ul className="space-y-2 mb-6 text-sm" data-id="element-1198">
                  <li className="flex items-start gap-2" data-id="element-1199">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1200" />
                    <span data-id="element-1201">10 Core Modules</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1202">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1203" />
                    <span data-id="element-1204">Content Framework Development</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1205">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1206" />
                    <span data-id="element-1207">AI Content Machine Setup</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1208">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1209" />
                    <span data-id="element-1210">Accountability Pod Membership</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1211">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1212" />
                    <span data-id="element-1213">2 Months Intensive Training</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1214">
                    <Check className="w-4 h-4 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1215" />
                    <span data-id="element-1216">Lifetime Updates & Support</span>
                  </li>
                </ul>

                <p className="text-xs font-mono text-[#9ca3af]" data-id="element-1217">
                  Perfect for professionals ready to build authority through
                  consistent content
                </p>
              </div>

              {/* One-on-One Drilling Pot Card */}
              <div className={`border-2 p-8 cursor-pointer transition-all ${selectedProgram === 'oneOnOne' ? 'border-salon-teal bg-salon-teal/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setSelectedProgram('oneOnOne')} data-id="element-1218">
                <div className="flex justify-between items-start mb-6" data-id="element-1219">
                  <div className="w-6 h-6 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1220">
                    {selectedProgram === 'oneOnOne' && <div className="w-4 h-4 rounded-full bg-salon-teal" data-id="element-1221" />}
                  </div>
                  <div className="bg-salon-teal text-white px-3 py-1 text-xs font-mono" data-id="element-1222">
                    PREMIUM
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4" data-id="element-1223">
                  One-on-One Drilling Pot
                </h3>

                <div className="mb-6" data-id="element-1224">
                  <div className="flex items-center justify-center gap-2 mb-2" data-id="element-1225">
                    <span className="text-xl text-[#9ca3af] line-through font-mono" data-id="element-1226">
                      ₦1,200,000
                    </span>
                  </div>
                  <div className="text-center mb-4" data-id="element-1227">
                    <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1228">
                      ₦700,000
                    </span>
                    <p className="text-xs font-mono text-[#9ca3af] mt-1" data-id="element-1229">
                      12-month private apprenticeship
                    </p>
                  </div>

                  {/* Payment Options for One-on-One */}
                  {selectedProgram === 'oneOnOne' && <div className="space-y-2 mb-4" data-id="element-1230">
                      <label className="flex items-center gap-3 p-3 border border-dashed border-[#9ca3af] hover:bg-white cursor-pointer" data-id="element-1231">
                        <input type="radio" name="oneOnOnePayment" checked={oneOnOnePaymentOption === 'annual'} onChange={() => setOneOnOnePaymentOption('annual')} className="text-salon-teal focus:ring-salon-teal" data-id="element-1232" />
                        <div className="flex-1" data-id="element-1233">
                          <div className="font-bold text-sm" data-id="element-1234">Pay in Full</div>
                          <div className="text-xs text-[#9ca3af]" data-id="element-1235">
                            ₦700,000 (Best Value)
                          </div>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 border border-dashed border-[#9ca3af] hover:bg-white cursor-pointer" data-id="element-1236">
                        <input type="radio" name="oneOnOnePayment" checked={oneOnOnePaymentOption === 'split'} onChange={() => setOneOnOnePaymentOption('split')} className="text-salon-teal focus:ring-salon-teal" data-id="element-1237" />
                        <div className="flex-1" data-id="element-1238">
                          <div className="font-bold text-sm" data-id="element-1239">Split Payment</div>
                          <div className="text-xs text-[#9ca3af]" data-id="element-1240">
                            ₦350,000 now + ₦350,000 month 2
                          </div>
                        </div>
                      </label>
                    </div>}
                </div>

                <ul className="space-y-2 mb-6 text-sm" data-id="element-1241">
                  <li className="flex items-start gap-2" data-id="element-1242">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1243" />
                    <span data-id="element-1244">Private 1-on-1 Sessions (2x/week)</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1245">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1246" />
                    <span data-id="element-1247">Personalized Content Strategy</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1248">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1249" />
                    <span data-id="element-1250">Direct Feedback & Guidance</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1251">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1252" />
                    <span data-id="element-1253">Custom AI Workflows</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1254">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1255" />
                    <span data-id="element-1256">12-Month Apprenticeship</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1257">
                    <Check className="w-4 h-4 text-salon-teal flex-shrink-0 mt-0.5" data-id="element-1258" />
                    <span data-id="element-1259">Faster Personal Velocity</span>
                  </li>
                </ul>

                <p className="text-xs font-mono text-[#9ca3af]" data-id="element-1260">
                  For those who want deep, personalized guidance and faster
                  results
                </p>
              </div>
            </div>

            {/* Payment Form */}
            <div className="max-w-md mx-auto mb-8" data-id="element-1261">
              <div className="space-y-4" data-id="element-1262">
                <div data-id="element-1263">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1264">
                    FULL_NAME
                  </label>
                  <input type="text" placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1265" />
                </div>
                <div data-id="element-1266">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1267">
                    EMAIL_ADDRESS
                  </label>
                  <input type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1268" />
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-1269">
              <Button size="large" onClick={handlePayment} className="bg-salon-red hover:bg-salon-red/90 border-salon-red min-w-[250px]" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1271" />} data-id="element-1270">
                {selectedProgram === 'group' ? 'COMPLETE_PAYMENT' : 'SUBMIT_APPLICATION'}
              </Button>

              <p className="font-mono text-xs text-[#9ca3af] mt-6" data-id="element-1272">
                Secure payment powered by Paystack
                <br data-id="element-1273" />
                {selectedProgram === 'oneOnOne' && oneOnOnePaymentOption === 'split' && <span className="text-salon-teal" data-id="element-1274">
                      Split payment: ₦350,000 today, ₦350,000 at month 2
                      <br data-id="element-1275" />
                    </span>}
                You'll be redirected to join our WhatsApp community after
                payment
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-1276" />
    </div>;
}